/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { IngestFile } from '../models/IngestFile';
import type { IngestFileStatusHistory } from '../models/IngestFileStatusHistory';
import type { IngestFileStatusUpdate } from '../models/IngestFileStatusUpdate';
import type { IngestFileUploadRequest } from '../models/IngestFileUploadRequest';
import type { IngestFileUploadResponse } from '../models/IngestFileUploadResponse';
import type { IngestRow } from '../models/IngestRow';
import type { IngestRowBulkAction } from '../models/IngestRowBulkAction';
import type { IngestRowBulkActionResult } from '../models/IngestRowBulkActionResult';
import type { PaginatedIngestColumnMapList } from '../models/PaginatedIngestColumnMapList';
import type { PaginatedIngestFileList } from '../models/PaginatedIngestFileList';
import type { PaginatedIngestRowList } from '../models/PaginatedIngestRowList';
import type { PatchedIngestFileStatusUpdate } from '../models/PatchedIngestFileStatusUpdate';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class IngestionService {

    /**
     * Read-only: lets the UI know whether a client has configured column
     * overrides for a report type (maps are edited in Django admin).
     * @returns PaginatedIngestColumnMapList
     * @throws ApiError
     */
    public static ingestionColumnMapsList({
        healthcareEntity,
        limit,
        offset,
        reportType,
    }: {
        healthcareEntity?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * * `schedule` - Schedule Data
         * * `time_pay` - Time & Pay Data
         */
        reportType?: 'schedule' | 'time_pay',
    }): CancelablePromise<PaginatedIngestColumnMapList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/ingestion/column-maps/',
            query: {
                'healthcare_entity': healthcareEntity,
                'limit': limit,
                'offset': offset,
                'report_type': reportType,
            },
        });
    }

    /**
     * @returns PaginatedIngestFileList
     * @throws ApiError
     */
    public static ingestionFilesList({
        currentStatus,
        currentStatusIn,
        healthcareEntity,
        limit,
        offset,
        ordering,
        reportType,
    }: {
        /**
         * Short code representation of the status for viewing and reports
         */
        currentStatus?: string,
        /**
         * Multiple values may be separated by commas.
         */
        currentStatusIn?: Array<string>,
        healthcareEntity?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * * `schedule` - Schedule Data
         * * `time_pay` - Time & Pay Data
         */
        reportType?: 'schedule' | 'time_pay',
    }): CancelablePromise<PaginatedIngestFileList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/ingestion/files/',
            query: {
                'current_status': currentStatus,
                'current_status__in': currentStatusIn,
                'healthcare_entity': healthcareEntity,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'report_type': reportType,
            },
        });
    }

    /**
     * @returns IngestFile
     * @throws ApiError
     */
    public static ingestionFilesRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this ingest file.
         */
        id: string,
    }): CancelablePromise<IngestFile> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/ingestion/files/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public static ingestionFilesDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this ingest file.
         */
        id: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/ingestion/files/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Confirm an ingest file upload
     * Verify the file landed in S3 and start processing. Processing is asynchronous; poll the file for status and row results.
     * @returns IngestFile
     * @throws ApiError
     */
    public static ingestionFilesConfirmCreate({
        id,
    }: {
        /**
         * A UUID string identifying this ingest file.
         */
        id: string,
    }): CancelablePromise<IngestFile> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/ingestion/files/{id}/confirm/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns IngestFileStatusHistory
     * @throws ApiError
     */
    public static ingestionFilesStatusHistoryRetrieve({
        id,
        sort,
    }: {
        /**
         * A UUID string identifying this ingest file.
         */
        id: string,
        /**
         * Sort direction. `asc` or `desc`
         */
        sort?: string,
    }): CancelablePromise<IngestFileStatusHistory> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/ingestion/files/{id}/status-history/',
            path: {
                'id': id,
            },
            query: {
                'sort': sort,
            },
        });
    }

    /**
     * @returns IngestFileStatusUpdate
     * @throws ApiError
     */
    public static ingestionFilesStatusUpdatePartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this ingest file.
         */
        id: string,
        requestBody?: PatchedIngestFileStatusUpdate,
    }): CancelablePromise<IngestFileStatusUpdate> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/ingestion/files/{id}/status-update/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Download a report template CSV
     * A CSV containing only the required header row — the client's configured column names when a healthcare_entity is given, otherwise our standard canonical headers.
     * @returns binary
     * @throws ApiError
     */
    public static ingestionFilesTemplateRetrieve({
        healthcareEntity,
        reportType,
    }: {
        /**
         * When given, headers use this client's configured column map.
         */
        healthcareEntity?: string,
        /**
         * Report type; defaults to schedule.
         */
        reportType?: string,
    }): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/ingestion/files/template/',
            query: {
                'healthcare_entity': healthcareEntity,
                'report_type': reportType,
            },
        });
    }

    /**
     * Request an ingest file upload
     * Create a PENDING ingest file and return a presigned PUT URL for uploading the raw report directly to S3.
     * @returns IngestFileUploadResponse
     * @throws ApiError
     */
    public static ingestionFilesUploadRequestCreate({
        requestBody,
    }: {
        requestBody: IngestFileUploadRequest,
    }): CancelablePromise<IngestFileUploadResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/ingestion/files/upload-request/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PaginatedIngestRowList
     * @throws ApiError
     */
    public static ingestionRowsList({
        errorCode,
        ingestFile,
        limit,
        offset,
        ordering,
        outcome,
        resolution,
    }: {
        /**
         * * `invalid_row` - Invalid Row
         * * `unknown_npi` - Unknown NPI
         * * `unknown_location` - Unknown Location
         * * `no_connection` - No Active Connection
         * * `no_assignment` - No Assignment
         * * `no_shift` - No Matching Shift
         * * `no_effective_rate` - No Effective Rate
         * * `unmapped_shift_rate_type` - Unmapped Shift Rate Type
         * * `availability_conflict` - Availability Conflict
         * * `shift_locked` - Shift Locked
         * * `timecard_locked` - Timecard Locked
         * * `missing_from_source` - Missing From Source
         * * `internal_error` - Internal Error
         */
        errorCode?: 'availability_conflict' | 'internal_error' | 'invalid_row' | 'missing_from_source' | 'no_assignment' | 'no_connection' | 'no_effective_rate' | 'no_shift' | 'shift_locked' | 'timecard_locked' | 'unknown_location' | 'unknown_npi' | 'unmapped_shift_rate_type',
        ingestFile?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * * `pending` - Pending
         * * `created` - Created
         * * `updated` - Updated
         * * `unchanged` - Unchanged
         * * `duplicate` - Duplicate
         * * `skipped` - Skipped
         * * `error` - Error
         */
        outcome?: 'created' | 'duplicate' | 'error' | 'pending' | 'skipped' | 'unchanged' | 'updated',
        /**
         * * `open` - Open
         * * `resolved` - Resolved
         * * `dismissed` - Dismissed
         */
        resolution?: 'dismissed' | 'open' | 'resolved',
    }): CancelablePromise<PaginatedIngestRowList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/ingestion/rows/',
            query: {
                'error_code': errorCode,
                'ingest_file': ingestFile,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'outcome': outcome,
                'resolution': resolution,
            },
        });
    }

    /**
     * @returns IngestRow
     * @throws ApiError
     */
    public static ingestionRowsRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this ingest row.
         */
        id: string,
    }): CancelablePromise<IngestRow> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/ingestion/rows/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Dismiss an errored ingest row
     * Mark this open error row as dismissed (no action will be taken).
     * @returns IngestRow
     * @throws ApiError
     */
    public static ingestionRowsDismissCreate({
        id,
    }: {
        /**
         * A UUID string identifying this ingest row.
         */
        id: string,
    }): CancelablePromise<IngestRow> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/ingestion/rows/{id}/dismiss/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Retry an errored ingest row
     * Re-run match and reconcile for this row against current data.
     * @returns IngestRow
     * @throws ApiError
     */
    public static ingestionRowsRetryCreate({
        id,
    }: {
        /**
         * A UUID string identifying this ingest row.
         */
        id: string,
    }): CancelablePromise<IngestRow> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/ingestion/rows/{id}/retry/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Dismiss errored ingest rows in bulk
     * Dismiss the given rows (or every open error row of a file). Marking only — re-evaluating rows after fixes is the Reprocess action on the file. Ineligible rows are skipped and counted.
     * @returns IngestRowBulkActionResult
     * @throws ApiError
     */
    public static ingestionRowsBulkDismissCreate({
        requestBody,
    }: {
        requestBody?: IngestRowBulkAction,
    }): CancelablePromise<IngestRowBulkActionResult> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/ingestion/rows/bulk-dismiss/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
